import { useState } from 'react';
import { api, authClient, formatDate, formatMoney, useApi } from '../api';
import { CopyButton } from '../components';
import { TeamCard } from './Team';

interface ApiKey {
  id: string;
  name: string;
  prefix: string;
  createdAt: string;
  lastUsedAt: string | null;
  revokedAt: string | null;
}

interface AccountInfo {
  id: string;
  name: string;
  state: string;
  createdAt: string;
  user: {
    email: string;
    name: string;
    role: string;
    emailVerified: boolean;
    teamRole: 'owner' | 'admin' | 'member';
  };
}

export function AccountPage() {
  const account = useApi<AccountInfo>('/account');
  const sessions = useApi<{
    sessions: {
      id: string;
      createdAt: string;
      ipAddress: string | null;
      userAgent: string | null;
      current: boolean;
    }[];
  }>('/account/sessions');
  const auditLog = useApi<{
    events: {
      id: string;
      action: string;
      targetType: string | null;
      targetId: string | null;
      createdAt: string;
    }[];
  }>('/account/audit');
  const [name, setName] = useState<string | null>(null);
  const [pw, setPw] = useState({ current: '', next: '' });
  const [msg, setMsg] = useState<string | null>(null);
  const keys = useApi<{ keys: ApiKey[] }>('/account/api-keys');
  const referral = useApi<{
    code: string;
    link: string;
    inviteeBonusMicroUsd: number;
    inviterBonusMicroUsd: number;
    qualifyingTopupMicroUsd: number;
    invited: number;
    rewarded: number;
    earnedMicroUsd: number;
  }>('/account/referral');
  const [keyName, setKeyName] = useState('');
  const [newSecret, setNewSecret] = useState<string | null>(null);
  const a = account.data;
  return (
    <div>
      <h1>Account</h1>
      <div className="grid cols-2">
        <div className="card">
          <h2>Details</h2>
          <div className="field">
            <label>Account name</label>
            <input value={name ?? a?.name ?? ''} onChange={(e) => setName(e.target.value)} />
          </div>
          <button
            onClick={async () => {
              await api('/account', { method: 'PATCH', json: { name: name ?? a?.name } });
              await account.reload();
              setMsg('Saved');
            }}
          >
            Save
          </button>
          <p className="muted" style={{ marginTop: 10 }}>
            Signed in as {a?.user.email} {a?.user.emailVerified ? '(verified)' : ''}
          </p>
        </div>
        <div className="card">
          <h2>Change password</h2>
          <div className="field">
            <label>Current password</label>
            <input
              type="password"
              value={pw.current}
              onChange={(e) => setPw({ ...pw, current: e.target.value })}
            />
          </div>
          <div className="field">
            <label>New password</label>
            <input
              type="password"
              minLength={10}
              value={pw.next}
              onChange={(e) => setPw({ ...pw, next: e.target.value })}
            />
          </div>
          <button
            onClick={async () => {
              const r = await authClient.changePassword({
                currentPassword: pw.current,
                newPassword: pw.next,
                revokeOtherSessions: true,
              });
              setMsg(r.error ? (r.error.message ?? 'Failed') : 'Password changed; other sessions signed out');
              setPw({ current: '', next: '' });
            }}
          >
            Update password
          </button>
        </div>
      </div>
      {msg && <p className="ok">{msg}</p>}
      {a && <TeamCard myRole={a.user.teamRole} />}
      <div className="card">
        <h2>Invite a friend</h2>
        {referral.data && (
          <>
            <p className="muted">
              Share your link. Your friend gets {formatMoney(referral.data.inviteeBonusMicroUsd)} of credit
              when they sign up, and you get {formatMoney(referral.data.inviterBonusMicroUsd)} once they add{' '}
              {formatMoney(referral.data.qualifyingTopupMicroUsd)} or more. Invites from the same device,
              network or email alias do not count.
            </p>
            <div className="row">
              <div className="secret" style={{ flex: 1, borderStyle: 'solid' }}>
                {referral.data.link}
              </div>
              <CopyButton text={referral.data.link} />
            </div>
            <p className="muted" style={{ marginTop: 8 }}>
              {referral.data.invited} invited · {referral.data.rewarded} rewarded ·{' '}
              {formatMoney(referral.data.earnedMicroUsd)} earned
            </p>
          </>
        )}
      </div>
      <div className="card">
        <h2>API keys</h2>
        <p className="muted">
          For scripts and AI agents. A key can do everything your account can on the API (create, connect,
          delete databases), except manage keys and sessions. Send it as{' '}
          <code>Authorization: Bearer tallypg_…</code>.
        </p>
        <form
          className="row"
          onSubmit={async (e) => {
            e.preventDefault();
            try {
              const r = await api<{ secret: string }>('/account/api-keys', {
                method: 'POST',
                json: { name: keyName },
              });
              setNewSecret(r.secret);
              setKeyName('');
              await keys.reload();
            } catch (err) {
              setMsg(err instanceof Error ? err.message : 'Failed');
            }
          }}
        >
          <input
            placeholder="key name, e.g. deploy-bot"
            value={keyName}
            onChange={(e) => setKeyName(e.target.value)}
            required
            maxLength={64}
            style={{ maxWidth: 320 }}
          />
          <button type="submit">Create key</button>
        </form>
        {newSecret && (
          <div style={{ marginTop: 12 }}>
            <div className="banner">Copy this key now. It will not be shown again.</div>
            <div className="row">
              <div className="secret" style={{ flex: 1 }}>
                {newSecret}
              </div>
              <CopyButton text={newSecret} />
            </div>
            <pre
              style={{ marginTop: 10 }}
            >{`curl -H "Authorization: Bearer ${newSecret}" ${window.location.origin}/api/v1/databases`}</pre>
          </div>
        )}
        <table style={{ marginTop: 12 }}>
          <thead>
            <tr>
              <th>Name</th>
              <th>Key</th>
              <th>Created</th>
              <th>Last used</th>
              <th />
            </tr>
          </thead>
          <tbody>
            {keys.data?.keys.map((k) => (
              <tr key={k.id}>
                <td>{k.name}</td>
                <td>
                  <code>{k.prefix}…</code>
                </td>
                <td>{formatDate(k.createdAt)}</td>
                <td>
                  {k.revokedAt ? <span className="badge failed">revoked</span> : formatDate(k.lastUsedAt)}
                </td>
                <td>
                  {!k.revokedAt && (
                    <button
                      className="danger"
                      onClick={async () => {
                        if (!confirm(`Revoke key "${k.name}"? Anything using it stops working immediately.`))
                          return;
                        await api(`/account/api-keys/${k.id}`, { method: 'DELETE' });
                        await keys.reload();
                      }}
                    >
                      Revoke
                    </button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <div className="card">
        <div className="row between">
          <h2>Sessions</h2>
          <button
            className="secondary"
            onClick={async () => {
              await api('/account/sessions/revoke-others', { method: 'POST' });
              await sessions.reload();
            }}
          >
            Sign out other sessions
          </button>
        </div>
        <table>
          <thead>
            <tr>
              <th>Started</th>
              <th>Address</th>
              <th>Client</th>
              <th />
            </tr>
          </thead>
          <tbody>
            {sessions.data?.sessions.map((s) => (
              <tr key={s.id}>
                <td>{formatDate(s.createdAt)}</td>
                <td>{s.ipAddress ?? '—'}</td>
                <td className="muted">{s.userAgent?.slice(0, 60) ?? '—'}</td>
                <td>{s.current ? <span className="badge active">this session</span> : null}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <div className="card">
        <h2>Security log</h2>
        <table>
          <thead>
            <tr>
              <th>When</th>
              <th>Event</th>
              <th>Target</th>
            </tr>
          </thead>
          <tbody>
            {auditLog.data?.events.map((e) => (
              <tr key={e.id}>
                <td>{formatDate(e.createdAt)}</td>
                <td>{e.action.replace(/_/g, ' ')}</td>
                <td className="muted">{e.targetId ?? ''}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
